// UI-WORKBENCH-1 (2026-05-07) — Right-panel preview for the Workbench.
// WAVE 1 (UI sprint): upgraded from a passive summary to an intelligence panel.
// A 2x2 "signals" strip (Readiness / Issues / Bills / Tasks, color-coded by the
// LD-UI-001 semantic system) surfaces "what needs action" at a glance, above the
// financial trio + detail + recent activity. Data comes from the existing
// lightweight GET /claims/{id}/preview (+ cheap open_bills / open_issues counts).
import { useState, useEffect } from "react";
import { apiFetch, fmt, statusColor } from "../api";
import { Spinner } from "./ui";  // UI-SWEEP-WORKBENCH

const sourceLabel = (s) => s === "vca" ? "VCA" : "App";
const sourceBg    = (s) => s === "vca" ? "#64748b" : "var(--navy)";

const TONE = {
  ok:    { fg: "#16a34a", bg: "#f0fdf4", br: "#16a34a" },
  amber: { fg: "#b45309", bg: "#fffbeb", br: "#d97706" },
  red:   { fg: "#b91c1c", bg: "#fef2f2", br: "#dc2626" },
  slate: { fg: "#475569", bg: "#f8fafc", br: "#94a3b8" },
};

function Signal({ label, value, tone }) {
  const t = TONE[tone] || TONE.slate;
  return (
    <div style={{ background: t.bg, borderLeft: `3px solid ${t.br}`, borderRadius: 4, padding: "6px 9px", minWidth: 0 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text3)" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: t.fg, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
    </div>
  );
}

const READINESS = {
  ready:           { short: "Ready",     tone: "ok" },
  partially_ready: { short: "Partial",   tone: "amber" },
  not_ready:       { short: "Not Ready", tone: "red" },
};

export default function PreviewPanel({ claimId, onOpenClaim, refreshKey }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    if (!claimId) { setData(null); return; }
    setLoading(true);
    setErr(null);
    apiFetch(`/claims/${claimId}/preview`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(String(e.message || e)); setLoading(false); });
  }, [claimId, refreshKey]);

  if (!claimId) {
    return (
      <div className="preview-empty">
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>◧</div>
        <div>Select a claim to preview</div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Reserves, readiness, bills &amp; issues at a glance — without opening the claim.</div>
      </div>
    );
  }
  if (loading) return <Spinner label="Loading preview…" />;
  if (err)     return <div className="alert-error" style={{ margin: 14 }}>{err}</div>;
  if (!data)   return null;

  const remaining = (parseFloat(data.total_reserves) || 0) - (parseFloat(data.total_paid) || 0);
  const open = data.claim_status === "Open";
  const remTone = remaining < 0 ? " neg" : "";
  const title = [data.insured_name, data.claimant_name].filter(Boolean).join(" — ") || data.claim_number;
  const sc = statusColor(data.claim_status);
  const rdy = READINESS[data.setup_readiness] || { short: "—", tone: "slate" };
  const issues = data.open_issues || 0;
  const bills  = data.open_bills  || 0;
  const tasks  = data.open_tasks  || 0;

  return (
    <>
      <div className="preview-header">
        <div className="preview-claim-num">{data.claim_number}</div>
        <div className="preview-claim-title">{title}</div>
        <span className="preview-claim-status"
              style={{ background: `${sc}35`, color: "white", border: `1px solid ${sc}60` }}>
          {data.claim_status}{data.litigation_flag && " ⚠ LITIGATION"}
        </span>
        {data.is_hipaa_sensitive && (
          <div style={{ fontSize: 10, marginTop: 6, color: "#fca5a5", fontWeight: 600 }}>⚠ HIPAA Sensitive</div>
        )}
      </div>

      <div className="preview-actions">
        <button className="btn btn-blue" onClick={() => onOpenClaim(claimId)}>Open Claim →</button>
      </div>

      {/* WAVE 1 signals strip — what needs action, at a glance */}
      <div className="preview-section">
        <div className="preview-section-title">Signals</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <Signal label="Readiness"  value={rdy.short} tone={rdy.tone} />
          <Signal label="Issues"     value={issues} tone={issues > 0 ? "red" : "ok"} />
          <Signal label="Open Bills" value={bills}  tone={bills  > 0 ? "amber" : "ok"} />
          <Signal label="Open Tasks" value={tasks}  tone={tasks  > 0 ? "amber" : "ok"} />
        </div>
      </div>

      <div className="preview-section">
        <div className="preview-section-title">Reserves</div>
        <div className="preview-trio">
          <div className="preview-trio-cell">
            <div className="preview-trio-label">Total Reserve</div>
            <div className="preview-trio-value mono">{fmt.money(data.total_reserves)}</div>
          </div>
          <div className="preview-trio-cell">
            <div className="preview-trio-label">Total Payments</div>
            <div className="preview-trio-value mono">{fmt.money(data.total_paid)}</div>
          </div>
          <div className="preview-trio-cell">
            <div className="preview-trio-label">Remaining</div>
            <div className={`preview-trio-value mono${remTone}`} style={{ color: remaining < 0 ? "var(--red)" : (remaining === 0 && open ? "#d97706" : undefined) }}>{fmt.money(remaining)}</div>
          </div>
        </div>
      </div>

      <div className="preview-section">
        <div className="preview-section-title">Detail</div>
        <div className="preview-meta-row"><span className="label">DOL</span>     <span className="value">{fmt.date(data.date_of_loss) || "—"}</span></div>
        <div className="preview-meta-row"><span className="label">Adjuster</span><span className="value">{data.lead_adjuster_name || "—"}</span></div>
        <div className="preview-meta-row"><span className="label">Vessel</span>  <span className="value">{data.vessel_name || "—"}</span></div>
        <div className="preview-meta-row"><span className="label">Type</span>    <span className="value">{data.claim_type_label || "—"}</span></div>
        <div className="preview-meta-row"><span className="label">Matter</span>  <span className="value">{data.matter_type ? data.matter_type.toUpperCase() : "—"}</span></div>
      </div>

      <div className="preview-section">
        <div className="preview-section-title">Recent Activity{data.diary && data.diary.length > 0 ? ` (${data.diary.length})` : ""}</div>
        {!data.diary || data.diary.length === 0 ? (
          <div style={{ fontSize: 11, color: "var(--text3)" }}>No diary entries yet</div>
        ) : data.diary.map(d => (
          <div key={d.id} className="preview-diary-entry">
            <div className="preview-diary-meta">
              <span className="preview-diary-source" style={{ background: sourceBg(d.source) }}>{sourceLabel(d.source)}</span>
              <span>{fmt.datetime(d.written_at)}</span>
              <span>·</span>
              <span style={{ fontWeight: 500 }}>{d.author_name || "Unknown"}</span>
              {d.is_internal  && <span style={{ color: "var(--red)", fontWeight: 600 }}>· Internal</span>}
              {d.is_important && <span style={{ color: "#d97706", fontWeight: 600 }}>· ★</span>}
            </div>
            <div className="preview-diary-body">{d.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}
